<template>
  <section id="quick-start" class="quick-start-section section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">🚀 Claude Code 快速入门指南</h2>
        <p class="section-subtitle">
          掌握这些基础命令和技巧，快速上手 Claude Code，提升开发效率
        </p>
      </div>

      <div class="guide-container">
        <!-- 基础启动 -->
        <div class="guide-section">
          <div class="section-icon">⚡</div>
          <div class="section-content">
            <h3 class="section-title">基础启动与更新</h3>
            <div class="command-grid">
              <div class="command-card">
                <h4>启动 Claude Code</h4>
                <div class="command-block">
                  <code class="command">claude</code>
                  <button class="copy-btn" @click="copyToClipboard('claude')">复制</button>
                </div>
                <p class="command-desc">运行此命令启动 Claude Code 应用程序，开始新的会话</p>
              </div>
              
              <div class="command-card">
                <h4>更新到最新版本</h4>
                <div class="command-block">
                  <code class="command">claude update</code>
                  <button class="copy-btn" @click="copyToClipboard('claude update')">复制</button>
                </div>
                <p class="command-desc">定期执行此命令获取最新功能和安全更新</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 会话管理 -->
        <div class="guide-section">
          <div class="section-icon">💬</div>
          <div class="section-content">
            <h3 class="section-title">会话管理</h3>
            <div class="command-grid">
              <div class="command-card">
                <h4>继续最近会话</h4>
                <div class="command-block">
                  <code class="command">claude -c</code>
                  <button class="copy-btn" @click="copyToClipboard('claude -c')">复制</button>
                </div>
                <p class="command-desc">快速恢复上一次未完成的对话</p>
              </div>
              
              <div class="command-card">
                <h4>查看会话历史</h4>
                <div class="command-block">
                  <code class="command">/resume</code>
                  <button class="copy-btn" @click="copyToClipboard('/resume')">复制</button>
                </div>
                <p class="command-desc">打开会话历史记录列表，可选择恢复之前的会话</p>
              </div>
              
              <div class="command-card">
                <h4>清除当前会话</h4>
                <div class="command-block">
                  <code class="command">/clear</code>
                  <button class="copy-btn" @click="copyToClipboard('/clear')">复制</button>
                </div>
                <p class="command-desc">清空当前会话的所有历史记录，开始全新对话</p>
              </div>
              
              <div class="command-card">
                <h4>总结并开启新会话</h4>
                <div class="command-block">
                  <code class="command">/compact</code>
                  <button class="copy-btn" @click="copyToClipboard('/compact')">复制</button>
                </div>
                <p class="command-desc">自动总结当前会话内容，并基于总结开启新会话</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 快捷操作 -->
        <div class="guide-section">
          <div class="section-icon">⌨️</div>
          <div class="section-content">
            <h3 class="section-title">快捷操作</h3>
            <div class="shortcut-grid">
              <div class="shortcut-card">
                <div class="shortcut-key">
                  <kbd>Ctrl</kbd> + <kbd>L</kbd>
                </div>
                <div class="shortcut-desc">
                  <h4>清屏（保留对话历史）</h4>
                  <p>清除屏幕显示但保留所有对话记录，让界面更整洁</p>
                </div>
              </div>
              
              <div class="shortcut-card">
                <div class="shortcut-key">
                  <kbd>Esc</kbd>
                </div>
                <div class="shortcut-desc">
                  <h4>暂停并对话</h4>
                  <p>暂停当前操作，进入对话输入模式</p>
                </div>
              </div>
              
              <div class="shortcut-card">
                <div class="shortcut-key">
                  <kbd>Esc</kbd> + <kbd>Esc</kbd>
                </div>
                <div class="shortcut-desc">
                  <h4>回退编辑</h4>
                  <p>返回上一条消息并进入编辑模式，方便修正输入内容</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 文档管理 -->
        <div class="guide-section">
          <div class="section-icon">📋</div>
          <div class="section-content">
            <h3 class="section-title">文档管理</h3>
            <div class="command-grid">
              <div class="command-card">
                <h4>初始化项目文档</h4>
                <div class="command-block">
                  <code class="command">/init</code>
                  <button class="copy-btn" @click="copyToClipboard('/init')">复制</button>
                </div>
                <p class="command-desc">自动扫描当前项目结构和规则，生成初始的 CLAUDE.md 文档</p>
              </div>
              
              <div class="command-card">
                <h4>添加内容到文档</h4>
                <div class="command-block">
                  <code class="command"># 你的内容</code>
                  <button class="copy-btn" @click="copyToClipboard('# 你的内容')">复制</button>
                </div>
                <p class="command-desc">在消息开头使用 # 前缀，可将该内容添加到 CLAUDE.md</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 高级功能 -->
        <div class="guide-section">
          <div class="section-icon">🧠</div>
          <div class="section-content">
            <h3 class="section-title">高级功能</h3>
            <div class="command-grid">
              <div class="command-card">
                <h4>创建智能代理</h4>
                <div class="command-block">
                  <code class="command">/agents</code>
                  <button class="copy-btn" @click="copyToClipboard('/agents')">复制</button>
                </div>
                <p class="command-desc">打开代理创建菜单，只需输入功能描述，Claude 会自动创建相应的代理</p>
              </div>
              
              <div class="command-card">
                <h4>任务管理</h4>
                <div class="command-block">
                  <code class="command">todo list</code>
                  <button class="copy-btn" @click="copyToClipboard('todo list')">复制</button>
                </div>
                <p class="command-desc">直接输入任务描述即可创建待办事项，系统会自动记录并管理</p>
              </div>
              
              <div class="command-card">
                <h4>深度思考模式</h4>
                <div class="command-block">
                  <code class="command">think hard</code>
                  <button class="copy-btn" @click="copyToClipboard('think hard')">复制</button>
                </div>
                <p class="command-desc">触发 Claude 的深度思考模式，对复杂问题提供更全面的分析</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 实用提示 -->
      <div class="tips-section">
        <h3 class="tips-title">💡 实用提示</h3>
        <div class="tips-grid">
          <div class="tip-card">
            <div class="tip-icon">🎯</div>
            <h4>项目根目录运行</h4>
            <p>在项目根目录启动 Claude Code，可以更好地理解项目结构和上下文</p>
          </div>
          
          <div class="tip-card">
            <div class="tip-icon">📝</div>
            <h4>详细描述需求</h4>
            <p>提供详细的问题描述和上下文，Claude 能给出更准确和有用的建议</p>
          </div>
          
          <div class="tip-card">
            <div class="tip-icon">🔄</div>
            <h4>善用会话管理</h4>
            <p>利用 /compact 和 /resume 命令管理长对话，保持上下文连贯性</p>
          </div>
          
          <div class="tip-card">
            <div class="tip-icon">⚡</div>
            <h4>快捷键提效</h4>
            <p>熟练使用 Ctrl+L 和 Esc 快捷键，可以大大提升使用体验</p>
          </div>
        </div>
      </div>

      <!-- 下一步操作 -->
      <div class="next-steps">
        <h3 class="next-title">🎯 下一步操作</h3>
        <div class="steps-flow">
          <div class="flow-step">
            <div class="step-number">1</div>
            <h4>购买套餐</h4>
            <p>选择适合的 GAC 套餐获取 API 密钥</p>
            <a href="#pricing" class="btn btn-primary" @click="$parent.setCurrentPage('home')">
              查看套餐
            </a>
          </div>
          
          <div class="flow-arrow">→</div>
          
          <div class="flow-step">
            <div class="step-number">2</div>
            <h4>安装配置</h4>
            <p>按照安装指南完成环境配置</p>
            <a href="#" class="btn btn-secondary" @click="$parent.setCurrentPage('install')">
              安装指南
            </a>
          </div>
          
          <div class="flow-arrow">→</div>
          
          <div class="flow-step">
            <div class="step-number">3</div>
            <h4>开始使用</h4>
            <p>运行 claude 命令开始体验</p>
            <div class="final-command">
              <code>claude</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'QuickStartSection',
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // 简单的反馈，可以后续优化为toast提示
        alert('已复制到剪贴板！');
      }).catch(() => {
        // 降级方案
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('已复制到剪贴板！');
      });
    }
  }
}
</script>

<style scoped>
.quick-start-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
}

.guide-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 60px;
}

.guide-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.section-icon {
  font-size: 3rem;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 8px;
}

.section-content {
  flex: 1;
}

.guide-section .section-title {
  font-size: 1.75rem;
  margin-bottom: 32px;
  text-align: left;
}

.command-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.command-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.command-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.command-card h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.command-block {
  position: relative;
  margin-bottom: 12px;
}

.command {
  background: #1e293b;
  color: #e2e8f0;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 0.875rem;
  display: block;
  margin-bottom: 8px;
  word-break: break-all;
}

.copy-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn:hover {
  background: #1d4ed8;
}

.command-desc {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

/* 快捷键样式 */
.shortcut-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.shortcut-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  gap: 24px;
  align-items: center;
}

.shortcut-key {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.shortcut-desc {
  flex: 1;
}

.shortcut-desc h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.shortcut-desc p {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

kbd {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 实用提示 */
.tips-section {
  margin-top: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.tips-title {
  text-align: center;
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 40px;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.tip-card {
  background: linear-gradient(135deg, #fef3c7, #fbbf24);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(251, 191, 36, 0.25);
}

.tip-icon {
  font-size: 2.5rem;
  margin-bottom: 16px;
}

.tip-card h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.tip-card p {
  color: #78350f;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

/* 下一步操作 */
.next-steps {
  margin-top: 80px;
  background: linear-gradient(135deg, #1e293b, #334155);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.next-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 40px;
}

.steps-flow {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
}

.flow-step {
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  max-width: 280px;
  flex: 1;
  min-width: 220px;
}

.step-number {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0 auto 16px;
}

.flow-step h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.flow-step p {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 20px;
}

.flow-arrow {
  font-size: 2rem;
  color: white;
  font-weight: bold;
}

.final-command {
  background: #1e293b;
  color: #e2e8f0;
  padding: 12px 20px;
  border-radius: 8px;
  font-family: 'SF Mono', Monaco, monospace;
  font-weight: 600;
  display: inline-block;
}

@media (max-width: 1024px) {
  .guide-section {
    flex-direction: column;
    gap: 20px;
  }
  
  .section-icon {
    text-align: center;
    margin-top: 0;
  }
  
  .guide-section .section-title {
    text-align: center;
  }
  
  .command-grid {
    grid-template-columns: 1fr;
  }
  
  .shortcut-card {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
  }
  
  .steps-flow {
    flex-direction: column;
    gap: 20px;
  }
  
  .flow-arrow {
    transform: rotate(90deg);
  }
  
  .next-steps {
    padding: 40px 20px;
  }
}
</style>